import React, { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import { Button } from '../components/ui/button';
import { useAgentsQuery } from '@cotera/client/app/hooks/use-agents-display-names';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { Loading, toast } from '@cotera/client/app/components';
import { useLinearBotSettingsModel } from './linear-bot-settings.model';
import { ChatBotMappingSectionLoading } from './chat-bot-mapping-section-loading';

const DEFAULT_AGENT_VALUE = '__default__';

export const LinearBotTeamAgentsSection: React.FC = () => {
  const model = useLinearBotSettingsModel();
  const status = useWatchableValue(model.status);
  const config = useWatchableValue(model.config);
  const pendingTeamId = useWatchableValue(model.pendingTeamId);
  const { data: agentsData } = useAgentsQuery();
  const agents = agentsData?.agents ?? [];
  const [draftTeamId, setDraftTeamId] = useState('');
  const [draftAgentId, setDraftAgentId] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const teamItems = useMemo(
    () =>
      config.teams.map((team) => ({
        value: team.teamId,
        label: team.teamName === team.teamId ? team.teamId : team.teamName,
      })),
    [config.teams]
  );

  const mappedTeams = config.teams.filter((team) => team.agentId !== null);

  if (status === 'loading') {
    return (
      <ChatBotMappingSectionLoading
        title="Team agents"
        description="Assign a Cotera agent per Linear team. Teams without an assignment use Coco's default Linear ticket agent."
      />
    );
  }

  if (config.installed !== true) {
    return null;
  }

  const handleTeamAgentChange = async (
    teamId: string,
    nextValue: string
  ): Promise<void> => {
    try {
      if (nextValue === DEFAULT_AGENT_VALUE) {
        await model.clearTeamAgent(teamId);
      } else {
        await model.setTeamAgent(teamId, nextValue);
      }
    } catch {
      toast.error('Failed to update Linear team agent');
    }
  };

  const handleRemoveMapping = async (teamId: string): Promise<void> => {
    try {
      await model.clearTeamAgent(teamId);
      toast.success('Team mapping removed');
    } catch {
      toast.error('Failed to remove Linear team mapping');
    }
  };

  const handleAddMapping = async (): Promise<void> => {
    if (draftTeamId.length === 0) {
      toast.error('Select a Linear team');
      return;
    }
    if (draftAgentId.length === 0) {
      toast.error('Select an agent');
      return;
    }

    setIsAdding(true);
    try {
      await model.setTeamAgent(draftTeamId, draftAgentId);
      setDraftTeamId('');
      setDraftAgentId('');
      toast.success('Team agent saved');
    } catch {
      toast.error('Failed to save team agent');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Team agents</p>
        <p className="text-xs text-muted-foreground">
          Assign a Cotera agent per Linear team. Teams without an assignment use
          Coco&apos;s default Linear ticket agent.
        </p>
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <p className="text-xs font-medium text-muted-foreground">
          Add team mapping
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">Linear team</p>
            <Combobox
              items={teamItems}
              value={draftTeamId}
              onChange={setDraftTeamId}
              placeholder="Search or enter team ID..."
              searchPlaceholder="Search teams..."
              emptyMessage="Type a team ID if none are listed"
              allowCustomValues={true}
              className="w-full"
            />
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">Agent</p>
            <Select value={draftAgentId} onValueChange={setDraftAgentId}>
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.title ?? agent.agentKey}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            className="h-9"
            disabled={isAdding}
            onClick={() => {
              void handleAddMapping();
            }}
          >
            {isAdding ? (
              <>
                <Loading.Spinner variant="sm" />
                Saving...
              </>
            ) : (
              'Save mapping'
            )}
          </Button>
        </div>
      </div>

      {config.teamsError !== undefined && (
        <p className="text-xs text-destructive">{config.teamsError}</p>
      )}

      {mappedTeams.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No team mappings yet. Add one above to route a Linear team to a
          specific agent.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Configured mappings
          </p>
          {mappedTeams.map((team) => {
            const selectValue =
              team.agentId !== null ? team.agentId : DEFAULT_AGENT_VALUE;
            const teamLabel =
              team.teamName === team.teamId ? team.teamId : team.teamName;

            return (
              <div
                key={team.teamId}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
              >
                <p className="text-sm">{teamLabel}</p>
                <div className="flex items-center gap-2">
                  <Select
                    value={selectValue}
                    disabled={pendingTeamId === team.teamId}
                    onValueChange={(nextValue) => {
                      void handleTeamAgentChange(team.teamId, nextValue);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[220px]">
                      <SelectValue placeholder="Default ticket agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_AGENT_VALUE}>
                        Default ticket agent
                      </SelectItem>
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.title ?? agent.agentKey}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={pendingTeamId === team.teamId}
                    aria-label="Remove team mapping"
                    title="Remove mapping"
                    onClick={() => {
                      void handleRemoveMapping(team.teamId);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
